import type { SupabaseClient } from "@supabase/supabase-js";

import { fetchCheckpointRollout, type CheckpointRolloutRow } from "../data/checkpoint-rollout";
import { fetchRouteRollout, type RouteRolloutRow } from "../data/rollout";
import { RAW_TOKEN_HANDLING_RULES } from "../data/token-minter";
import { useReadModel } from "../data/useReadModel";
import { ActiveMaterialInventory } from "./ActiveMaterialInventory";
import { useTokenMintSession } from "./token-mint-session";

// Live token minting: route kits + per-checkpoint recovery. Confirm and one-time kit use
// centered GlassModal via TokenMintSessionProvider on PatrolsPage.

export function TokenMinter({
  client,
  accountId,
  canMint,
}: {
  client: SupabaseClient;
  accountId: string;
  canMint: boolean;
}) {
  const session = useTokenMintSession();
  const mintGeneration = session?.mintGeneration ?? 0;

  const routeRead = useReadModel<RouteRolloutRow[]>(
    () => (canMint ? fetchRouteRollout(client, accountId) : Promise.resolve([])),
    [client, accountId, mintGeneration, canMint],
  );
  const checkpointRead = useReadModel<CheckpointRolloutRow[]>(
    () => (canMint ? fetchCheckpointRollout(client, accountId) : Promise.resolve([])),
    [client, accountId, mintGeneration, canMint],
  );

  if (!canMint) {
    return (
      <p className="surface__state" role="status">
        Token minting requires the tokens.mint capability.
      </p>
    );
  }

  if (!session?.enabled) {
    return (
      <p className="surface__state surface__state--error" role="alert">
        Mint session is not available on this page.
      </p>
    );
  }

  const { openRouteConfirm, openCheckpointConfirm, hasSessionKit, hasRouteSessionKit, openSessionKit, openRouteSessionKit } = session;

  if (routeRead.status === "loading" && routeRead.data === null) {
    return (
      <p className="surface__state" role="status">
        Loading routes…
      </p>
    );
  }

  if (routeRead.status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {routeRead.error ?? "Could not load routes."}
      </p>
    );
  }

  const routes = routeRead.data ?? [];
  const recoveryCheckpoints = (checkpointRead.data ?? []).filter(
    (checkpoint) => checkpoint.operationalStatus === "active" && !checkpoint.hasCheckpointMaterial,
  );
  const replacementCheckpoints = (checkpointRead.data ?? []).filter(
    (checkpoint) => checkpoint.operationalStatus === "active" && checkpoint.hasCheckpointMaterial,
  );

  return (
    <div className="minter">
      <ul className="minter__rules" aria-label="Raw token handling">
        {RAW_TOKEN_HANDLING_RULES.map((rule) => (
          <li key={rule} className="minter__rule">
            {rule}
          </li>
        ))}
      </ul>

      <ActiveMaterialInventory rows={checkpointRead.data ?? []} />

      {routes.length === 0 ? (
        <p className="surface__state" role="status">
          No routes yet. Create a route in Setup before minting checkpoint kits.
        </p>
      ) : (
        <ul className="minter__routes" aria-label="Routes available for minting">
          {routes.map((route) => (
            <li key={route.routeId} className="minter__route">
              <div className="minter__route-names">
                <span className="minter__route-name">{route.routeName ?? "Route"}</span>
                <span className="minter__route-site">
                  {[route.siteName, route.clientName].filter(Boolean).join(" · ") || "No site"}
                </span>
              </div>
              <div className="minter__route-counts">
                {route.requiredCount} required · {route.readyCount} ready · {route.missingMaterialCount} missing
                material
              </div>
              <div className="minter__route-actions">
                {hasRouteSessionKit(route.routeId) ? (
                  <button
                    type="button"
                    className="builder__navbtn"
                    aria-label={`View kit for ${route.routeName ?? "route"}`}
                    onClick={() => openRouteSessionKit(route.routeId)}
                  >
                    View kit
                  </button>
                ) : null}
                {route.missingMaterialCount > 0 ? (
                  <button
                    type="button"
                    className="minter__confirm-go"
                    onClick={() =>
                      openRouteConfirm({
                        routeId: route.routeId,
                        routeName: route.routeName ?? "Route",
                        replace: false,
                        hasActiveMaterial: false,
                      })
                    }
                  >
                    Mint kit
                  </button>
                ) : null}
                {route.missingMaterialCount === 0 && route.requiredCount > 0 ? (
                  <button
                    type="button"
                    className="builder__navbtn"
                    onClick={() =>
                      openRouteConfirm({
                        routeId: route.routeId,
                        routeName: route.routeName ?? "Route",
                        replace: true,
                        hasActiveMaterial: true,
                      })
                    }
                  >
                    Replace tokens
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {checkpointRead.status === "error" ? (
        <p className="surface__state surface__state--error" role="alert">
          {checkpointRead.error ?? "Could not load checkpoint recovery targets."}
        </p>
      ) : recoveryCheckpoints.length > 0 || replacementCheckpoints.length > 0 ? (
        <section className="minter__recovery" aria-label="Checkpoint material recovery">
          <div className="minter__recovery-head">
            <h3 className="minter__section-title">Checkpoint material recovery</h3>
            <p className="minter__section-copy">
              Recreate missing checkpoint material or replace one checkpoint without reminting the full
              route kit.
            </p>
          </div>
          <ul className="minter__checkpoints">
            {[...recoveryCheckpoints, ...replacementCheckpoints].map((checkpoint) => {
              const hasMaterial = checkpoint.hasCheckpointMaterial;
              const routeLabel = [checkpoint.routeName, checkpoint.siteName].filter(Boolean).join(" · ");
              return (
                <li key={checkpoint.checkpointId} className="minter__checkpoint">
                  <div className="minter__checkpoint-main">
                    <span className="minter__checkpoint-name">{checkpoint.checkpointName}</span>
                    <span className="minter__checkpoint-context">{routeLabel || "No route"}</span>
                    <span className="minter__checkpoint-status">
                      {hasMaterial ? "Checkpoint material active" : "No checkpoint material yet"}
                    </span>
                    <span className="minter__checkpoint-hint">
                      {hasMaterial
                        ? "The server never re-displays an existing secret. If the kit is gone from this tab, Replace mints a new one and retires the mounted tag."
                        : "Creates one checkpoint token for print/export or external NFC write."}
                    </span>
                  </div>
                  <div className="minter__checkpoint-actions">
                    {hasSessionKit(checkpoint.checkpointId) ? (
                      <button
                        type="button"
                        className="builder__navbtn"
                        aria-label={`View kit for ${checkpoint.checkpointName}`}
                        onClick={() => openSessionKit(checkpoint.checkpointId)}
                      >
                        View kit
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className={hasMaterial ? "builder__navbtn" : "minter__confirm-go"}
                      aria-label={
                        hasMaterial
                          ? `Replace material for ${checkpoint.checkpointName}`
                          : `Create material for ${checkpoint.checkpointName}`
                      }
                      onClick={() =>
                        openCheckpointConfirm({
                          checkpointId: checkpoint.checkpointId,
                          checkpointName: checkpoint.checkpointName,
                          routeLabel,
                          hasExistingMaterial: hasMaterial,
                          replace: hasMaterial,
                        })
                      }
                    >
                      {hasMaterial ? "Replace material" : "Create material"}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
